/**
 * Super admin → Families → a family → "New user + welcome email".
 * In one go: make the person's website login (set-password link) and/or phone
 * app login (user name + password) in this family, then show one welcome
 * email with everything (English, Bangla or both) to copy, open in the email
 * app or share, and the same as an A4 sheet to print. See welcomePack.ts.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import QRCode from 'qrcode';
import { USERNAME_RE, type Role } from '@bhorosha/data';
import { t, tErr } from '@bhorosha/i18n';
import { backend } from '../data';
import { ErrorText, Field, Notice } from '../ui/components';
import { manualUrl, welcomeUrl } from '../inviteEmail';
import { welcomeEmail, welcomeSheet, type WelcomeInput, type WelcomeLang } from '../welcomePack';
import { signInLink } from './AppLogins';

type P = { id: string; name: string };
const APP_LINK_KEY = 'bh.admin.appLink';
const DEFAULT_APP_LINK = (import.meta.env.VITE_MOBILE_APP_URL as string | undefined) || '';

function suggestUsername(name: string) {
  const first = name.trim().toLowerCase().split(/\s+/)[0] || '';
  return first.normalize('NFKD').replace(/[^a-z0-9._-]/g, '').slice(0, 30);
}
async function copy(text: string) {
  try { await navigator.clipboard.writeText(text); return true; } catch { prompt('Copy this text', text); return false; }
}

export function WelcomeWizard({ fid, familyName, people, hasOwner, adminName, onDone, onClose }: {
  fid: string; familyName: string; people: P[]; hasOwner: boolean; adminName: string; onDone: () => void; onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [makeWeb, setMakeWeb] = useState(true);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>(hasOwner ? 'editor' : 'owner');
  const [makePhone, setMakePhone] = useState(true);
  const [username, setUsername] = useState('');
  const [userEdited, setUserEdited] = useState(false);
  const [all, setAll] = useState(people.length !== 1);
  const [ids, setIds] = useState<string[]>(people.length === 1 ? [people[0].id] : []);
  const [lang, setLang] = useState<WelcomeLang>('both');
  const [appLink, setAppLink] = useState(() => { try { return localStorage.getItem(APP_LINK_KEY) || DEFAULT_APP_LINK; } catch { return DEFAULT_APP_LINK; } });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [made, setMade] = useState<Omit<WelcomeInput, 'lang'> | null>(null);
  const [partial, setPartial] = useState('');

  useEffect(() => { if (!userEdited) setUsername(suggestUsername(name)); }, [name, userEdited]);

  async function create(e: FormEvent) {
    e.preventDefault(); setErr(''); setPartial('');
    if (!makeWeb && !makePhone) { setErr(t('wel.pickOne')); return; }
    const uname = username.trim().toLowerCase();
    if (makePhone && !USERNAME_RE.test(uname)) { setErr(t('wel.badUsername')); return; }
    if (makePhone && !all && !ids.length) { setErr(t('wel.pickPerson')); return; }
    setBusy(true);
    try { localStorage.setItem(APP_LINK_KEY, appLink.trim()); } catch { /* ignore */ }
    const out: Omit<WelcomeInput, 'lang'> = { name: name.trim(), familyName, adminName, site: location.origin, manual: manualUrl(), appLink: appLink.trim() };
    try {
      if (makeWeb) {
        const r = await backend.adminUserAction({ action: 'create', name: name.trim(), email: email.trim(), familyId: fid, role: role === 'viewer' ? 'viewer' : 'editor' });
        if (role === 'owner' && r.userId) await backend.adminSetMember(fid, r.userId, 'owner', true, []);
        out.web = { email: email.trim().toLowerCase(), link: welcomeUrl(r.token!), expiresAt: r.expiresAt! };
      }
      if (makePhone) {
        try {
          const r = await backend.appLoginAction({ action: 'family_create_login', familyId: fid, username: uname, name: name.trim(), role: 'editor', allPatients: all, patientIds: all ? [] : ids });
          out.phone = { username: r.username || uname, password: r.password!, forPeople: all ? (people.length === 1 ? people[0].name : familyName) : ids.map(id => people.find(p => p.id === id)?.name || '').join(', ') };
        } catch (ex) {
          if (!out.web) throw ex;
          setPartial(t('wel.phoneFailed', { msg: tErr(ex) }));
        }
      }
      setMade(out); onDone();
    } catch (ex) { setErr(tErr(ex)); } finally { setBusy(false); }
  }

  if (made) return <WelcomeResult input={made} lang={lang} setLang={setLang} partial={partial} onClose={onClose} />;

  return (
    <form className="bh-form bh-welcome" onSubmit={create}>
      <div className="bh-row" style={{ justifyContent: 'space-between' }}>
        <h4 style={{ margin: 0 }}>✉️ {t('wel.title', { family: familyName })}</h4>
        <button type="button" className="bh-icon-btn" onClick={onClose} aria-label={t('common.close')}>&times;</button>
      </div>
      <p className="bh-small bh-muted">{t('wel.help')}</p>
      <Field label={t('wel.name')}><input value={name} onChange={e => setName(e.target.value)} required placeholder="e.g. Saiara Begum" /></Field>

      <label className="bh-check bh-strong"><input type="checkbox" checked={makeWeb} onChange={e => setMakeWeb(e.target.checked)} /> 🌐 {t('wel.web')}</label>
      {makeWeb && <div className="bh-welcome-sub">
        <Field label={t('adm.userEmail')}><input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="name@example.com" autoCapitalize="none" /></Field>
        <Field label={t('fam.invite.role')}>
          <select value={role} onChange={e => setRole(e.target.value as Role)}>
            {!hasOwner && <option value="owner">{t('role.owner')}</option>}
            <option value="editor">{t('role.editor')}</option>
            <option value="viewer">{t('role.viewer')}</option>
          </select>
        </Field>
      </div>}

      <label className="bh-check bh-strong"><input type="checkbox" checked={makePhone} onChange={e => setMakePhone(e.target.checked)} /> 📱 {t('wel.phone')}</label>
      {makePhone && <div className="bh-welcome-sub">
        <Field label={t('app.username')} hint={t('wel.usernameHint')}>
          <input value={username} onChange={e => { setUsername(e.target.value.toLowerCase()); setUserEdited(true); }} required autoCapitalize="none" autoCorrect="off" spellCheck={false} />
        </Field>
        <Field label={t('wel.forWhom')}>
          <div className="bh-checks">
            <label className="bh-check"><input type="checkbox" checked={all} onChange={e => setAll(e.target.checked)} /> {t('fam.access.all')}</label>
            {!all && people.map(p => <label key={p.id} className="bh-check"><input type="checkbox" checked={ids.includes(p.id)} onChange={e => setIds(e.target.checked ? [...ids, p.id] : ids.filter(x => x !== p.id))} /> {p.name}</label>)}
          </div>
        </Field>
        <Field label={t('wel.appLink')} hint={t('wel.appLinkHint')}><input value={appLink} onChange={e => setAppLink(e.target.value)} placeholder="https://expo.dev/artifacts/…" autoCapitalize="none" /></Field>
      </div>}

      <Field label={t('wel.lang')}>
        <div className="bh-seg">
          {(['both', 'bn', 'en'] as WelcomeLang[]).map(l => <button type="button" key={l} className={lang === l ? 'on' : ''} onClick={() => setLang(l)}>{t(`wel.lang.${l}`)}</button>)}
        </div>
      </Field>
      <ErrorText>{err}</ErrorText>
      <div className="bh-row">
        <button className="bh-btn bh-btn-primary" disabled={busy}>{busy ? t('common.loading') : t('wel.create')}</button>
        <button type="button" className="bh-btn bh-btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
      </div>
    </form>
  );
}

export function WelcomeResult({ input, lang, setLang, partial, onClose }: {
  input: Omit<WelcomeInput, 'lang'>; lang: WelcomeLang; setLang: (l: WelcomeLang) => void; partial: string; onClose: () => void;
}) {
  const full: WelcomeInput = { ...input, lang };
  const mail = useMemo(() => welcomeEmail(full), [lang, input]); // eslint-disable-line react-hooks/exhaustive-deps
  const [note, setNote] = useState('');
  const flash = (s: string) => { setNote(s); setTimeout(() => setNote(''), 2500); };

  async function printSheet() {
    const q = (s: string) => QRCode.toDataURL(s, { margin: 1, width: 240 }).catch(() => '');
    const [web, app, phone] = await Promise.all([
      input.web ? q(input.web.link || input.site) : Promise.resolve(''),
      input.phone && input.appLink ? q(input.appLink) : Promise.resolve(''),
      input.phone ? q(signInLink(input.phone.username, input.phone.password)) : Promise.resolve(''),
    ]);
    const w = window.open('', '_blank');
    if (!w) { alert(t('wel.popup')); return; }
    w.document.write(welcomeSheet(full, { web: web || undefined, app: app || undefined, phone: phone || undefined }));
    w.document.close();
  }
  const mailto = `mailto:${encodeURIComponent(input.web?.email || '')}?subject=${encodeURIComponent(mail.subject)}&body=${encodeURIComponent(mail.body)}`;

  return (
    <div className="bh-welcome bh-invite-card">
      <div className="bh-row" style={{ justifyContent: 'space-between' }}>
        <h4 style={{ margin: 0 }}>✉️ {t('wel.ready', { name: input.name })}</h4>
        <button className="bh-icon-btn" onClick={onClose} aria-label={t('common.close')}>&times;</button>
      </div>
      <Notice tone="ok">{[input.web && t('wel.madeWeb', { email: input.web.email }), input.phone && t('wel.madePhone', { username: input.phone.username })].filter(Boolean).join(' · ')}</Notice>
      {partial && <Notice tone="warn">{partial}</Notice>}
      <div className="bh-seg" style={{ margin: '8px 0' }}>
        {(['both', 'bn', 'en'] as WelcomeLang[]).map(l => <button type="button" key={l} className={lang === l ? 'on' : ''} onClick={() => setLang(l)}>{t(`wel.lang.${l}`)}</button>)}
      </div>
      <Field label={t('wel.subject')}><input readOnly value={mail.subject} onFocus={e => e.target.select()} /></Field>
      <Field label={t('wel.body')}><textarea readOnly value={mail.body} rows={14} className="bh-welcome-body" onFocus={e => e.target.select()} /></Field>
      {note && <Notice tone="ok">{note}</Notice>}
      <div className="bh-row bh-welcome-actions">
        <button className="bh-btn bh-btn-primary" onClick={async () => { await copy(mail.full); flash(t('adm.copiedEmail')); }}>📋 {t('wel.copy')}</button>
        <a className="bh-btn bh-btn-ghost" href={mailto}>📧 {t('wel.openMail')}</a>
        {typeof navigator.share === 'function' && <button className="bh-btn bh-btn-ghost" onClick={() => navigator.share({ title: mail.subject, text: mail.body }).catch(() => {})}>📤 {t('common.share')}</button>}
        <button className="bh-btn bh-btn-ghost" onClick={printSheet}>🖨 {t('wel.print')}</button>
      </div>
      <p className="bh-small bh-muted">{t('wel.private')}</p>
    </div>
  );
}

// ---------------- New family, step by step ----------------
type StepState = 'todo' | 'busy' | 'done' | 'fail' | 'skip';
const STEPS = ['user', 'family', 'owner', 'phone'] as const;

/**
 * Super admin → Families → "New family (step by step)":
 *   1. create the user (name + email)
 *   2. create a family for that user
 *   3. make them the owner, with a one-time password (they choose their own at first sign-in)
 *   4. create the phone app user with a fixed password (stays until reset)
 *   5. write the welcome email with everything for both (Bangla / English) to send.
 */
export function NewFamilyWizard({ adminName, onCreated, onClose }: { adminName: string; onCreated: (familyId: string) => void; onClose?: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [famEdited, setFamEdited] = useState(false);
  const [makePhone, setMakePhone] = useState(true);
  const [phoneName, setPhoneName] = useState('');
  const [username, setUsername] = useState('');
  const [userEdited, setUserEdited] = useState(false);
  const [password, setPassword] = useState('');
  const [lang, setLang] = useState<WelcomeLang>('both');
  const [appLink, setAppLink] = useState(() => { try { return localStorage.getItem(APP_LINK_KEY) || DEFAULT_APP_LINK; } catch { return DEFAULT_APP_LINK; } });
  const [steps, setSteps] = useState<Record<(typeof STEPS)[number], StepState>>({ user: 'todo', family: 'todo', owner: 'todo', phone: 'todo' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [made, setMade] = useState<Omit<WelcomeInput, 'lang'> | null>(null);
  const [partial, setPartial] = useState('');

  useEffect(() => { if (!famEdited) { const last = name.trim().split(/\s+/).slice(-1)[0]; setFamilyName(last ? `${last} family` : ''); } }, [name, famEdited]);
  useEffect(() => { if (!userEdited) setUsername(suggestUsername(phoneName || name)); }, [phoneName, name, userEdited]);

  async function run(e: FormEvent) {
    e.preventDefault(); setErr(''); setPartial('');
    const uname = username.trim().toLowerCase();
    if (makePhone && !USERNAME_RE.test(uname)) { setErr(t('wel.badUsername')); return; }
    if (makePhone && password && password.trim().length < 8) { setErr(t('nf.pwShort')); return; }
    setBusy(true);
    try { localStorage.setItem(APP_LINK_KEY, appLink.trim()); } catch { /* ignore */ }
    const mark = (k: (typeof STEPS)[number], v: StepState) => setSteps(st => ({ ...st, [k]: v }));
    let at: (typeof STEPS)[number] = 'user';
    let fid = '';
    const out: Omit<WelcomeInput, 'lang'> = { name: name.trim(), familyName: familyName.trim(), adminName, site: location.origin, manual: manualUrl(), appLink: appLink.trim(), newFamily: true };
    try {
      mark('user', 'busy');
      const u = await backend.adminUserAction({ action: 'create', name: name.trim(), email: email.trim() });
      mark('user', 'done'); at = 'family'; mark('family', 'busy');
      fid = await backend.adminCreateFamily(familyName.trim(), email.trim().toLowerCase());
      mark('family', 'done'); at = 'owner'; mark('owner', 'busy');
      const o = await backend.adminUserAction({ action: 'activate', userId: u.userId! });
      out.web = o.tempPassword
        ? { email: email.trim().toLowerCase(), tempPassword: o.tempPassword, expiresAt: o.expiresAt! }
        : { email: email.trim().toLowerCase(), link: welcomeUrl(u.token!), expiresAt: u.expiresAt! };
      mark('owner', 'done');
      if (makePhone) {
        at = 'phone'; mark('phone', 'busy');
        const r = await backend.appLoginAction({ action: 'family_create_login', familyId: fid, username: uname, name: (phoneName || name).trim(), password: password.trim() || undefined, role: 'editor', allPatients: true, patientIds: [] });
        out.phone = { username: r.username || uname, password: r.password!, forPeople: (phoneName || name).trim() };
        mark('phone', 'done');
      } else mark('phone', 'skip');
      setMade(out); onCreated(fid);
    } catch (ex) {
      mark(at, 'fail');
      const msg = tErr(ex);
      if (out.web) { setPartial(t('nf.phoneFailed', { msg })); setMade(out); if (fid) onCreated(fid); }
      else { setErr(t('nf.failedAt', { step: t(`nf.step.${at}`), msg })); if (fid) onCreated(fid); }
    } finally { setBusy(false); }
  }

  const Progress = () => (
    <ol className="bh-nf-steps">
      {STEPS.map(k => <li key={k} className={steps[k]}><span>{{ todo: '○', busy: '⏳', done: '✓', fail: '✕', skip: '–' }[steps[k]]}</span> {t(`nf.step.${k}`)}</li>)}
      <li className={made ? 'done' : 'todo'}><span>{made ? '✓' : '○'}</span> {t('nf.step.email')}</li>
    </ol>
  );

  const started = Object.values(steps).some(v => v !== 'todo');
  if (made) return <div className="bh-nf"><Progress /><WelcomeResult input={made} lang={lang} setLang={setLang} partial={partial} onClose={onClose || (() => { setMade(null); setSteps({ user: 'todo', family: 'todo', owner: 'todo', phone: 'todo' }); setName(''); setEmail(''); setPhoneName(''); setPassword(''); setFamEdited(false); setUserEdited(false); })} /></div>;

  return (
    <form className="bh-nf" onSubmit={run}>
      <div className="bh-nf-head">
        <div>
          <h3 className="bh-h3" style={{ margin: 0 }}>➕ {t('nf.title')}</h3>
          <p className="bh-small bh-muted" style={{ margin: '2px 0 0' }}>{t('nf.help')}</p>
        </div>
        {onClose && <button type="button" className="bh-icon-btn" onClick={onClose} aria-label={t('common.close')}>&times;</button>}
      </div>

      <section className="bh-nf-sec">
        <div className="bh-nf-sec-h"><span className="bh-nf-ico">👤</span><div><b>{t('nf.sec.owner')}</b><span>{t('nf.sec.ownerSub')}</span></div></div>
        <div className="bh-nf-grid">
          <Field label={t('wel.name')}><input value={name} onChange={e => setName(e.target.value)} required placeholder="e.g. Tanvir Afzal" /></Field>
          <Field label={t('adm.userEmail')}><input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="name@example.com" autoCapitalize="none" /></Field>
        </div>
        <Field label={t('nf.familyName')} hint={t('nf.familyHint')}><input value={familyName} onChange={e => { setFamilyName(e.target.value); setFamEdited(true); }} required placeholder="e.g. Afzal family" /></Field>
        <div className="bh-nf-note">🔑 {t('nf.ownerHelp')}</div>
      </section>

      <section className={`bh-nf-sec${makePhone ? '' : ' off'}`}>
        <label className="bh-nf-sec-h bh-nf-toggle">
          <span className="bh-nf-ico">📱</span>
          <div><b>{t('nf.sec.phone')}</b><span>{t('nf.sec.phoneSub')}</span></div>
          <input type="checkbox" checked={makePhone} onChange={e => setMakePhone(e.target.checked)} aria-label={t('nf.step.phone')} />
        </label>
        {makePhone && <>
          <div className="bh-nf-grid">
            <Field label={t('nf.phoneName')} hint={t('nf.phoneNameHint')}><input value={phoneName} onChange={e => setPhoneName(e.target.value)} placeholder={name || 'e.g. Saiara Begum'} /></Field>
            <Field label={t('app.username')} hint={t('wel.usernameHint')}>
              <input value={username} onChange={e => { setUsername(e.target.value.toLowerCase()); setUserEdited(true); }} required autoCapitalize="none" autoCorrect="off" spellCheck={false} />
            </Field>
            <Field label={t('app.password')} hint={t('nf.pwHint')}><input value={password} onChange={e => setPassword(e.target.value)} autoCapitalize="none" autoCorrect="off" spellCheck={false} placeholder={t('nf.pwPlaceholder')} /></Field>
            <Field label={t('wel.appLink')} hint={t('wel.appLinkHint')}><input value={appLink} onChange={e => setAppLink(e.target.value)} placeholder="https://expo.dev/artifacts/…" autoCapitalize="none" /></Field>
          </div>
        </>}
      </section>

      <section className="bh-nf-sec">
        <div className="bh-nf-sec-h"><span className="bh-nf-ico">✉️</span><div><b>{t('nf.sec.email')}</b><span>{t('nf.sec.emailSub')}</span></div></div>
        <div className="bh-seg">
          {(['both', 'bn', 'en'] as WelcomeLang[]).map(l => <button type="button" key={l} className={lang === l ? 'on' : ''} onClick={() => setLang(l)}>{t(`wel.lang.${l}`)}</button>)}
        </div>
      </section>

      {started && <Progress />}
      <ErrorText>{err}</ErrorText>
      <div className="bh-row">
        <button className="bh-btn bh-btn-primary bh-nf-go" disabled={busy}>{busy ? t('common.loading') : t('nf.run')}</button>
        {onClose && <button type="button" className="bh-btn bh-btn-ghost" onClick={onClose}>{t('common.cancel')}</button>}
      </div>
    </form>
  );
}
